import { Link, createFileRoute } from "@tanstack/react-router";
import { ExpandableIntro } from "@/components/expandable-intro";

import { ArticleBody, ArticleToc } from "@/components/article-body";
import { AuthorByline } from "@/components/author-byline";
import { CasinoList } from "@/components/casino-list";
import { EditorialTeam } from "@/components/editorial-team";
import { EeatTrustBlocks, HowWeTested } from "@/components/eeat-blocks";
import { PayoutSpeedInfographic } from "@/components/infographics";
import { KeyTakeaways } from "@/components/key-takeaways";
import { QualityGuarantee } from "@/components/quality-guarantee";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { guide } from "@/content/guides/twintig-euro";
import { ARTICLE_IMAGES } from "@/content/images";
import { getAuthor } from "@/lib/authors";
import { LinkBudget, linkifyText } from "@/lib/internal-links";
import { ogImageMeta } from "@/lib/og";
import { getPublicCasinos } from "@/lib/queries";
import { SITE } from "@/lib/site";

const PATH = "/lage-storting/20-euro-deposit-casinos";
const URL = `${SITE.url}${PATH}/`;

export const Route = createFileRoute("/lage-storting/20-euro-deposit-casinos")({
  head: () => {
    const author = getAuthor(guide.authorSlug);
    const reviewer = getAuthor(guide.reviewerSlug);
    return {
      meta: [
        { title: "20 Euro Deposit Casino: Het Meest Gangbare Minimum" },
        { name: "description", content: guide.description },
        { property: "og:title", content: "20 Euro Deposit Casino: Het Meest Gangbare Minimum" },
        { property: "og:description", content: guide.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: URL },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "index, follow, max-image-preview:large" },
        ...ogImageMeta("20-euro-storten"),
      ],
      links: [{ rel: "canonical", href: URL }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: guide.title,
            description: guide.description,
            inLanguage: "nl-NL",
            mainEntityOfPage: URL,
            dateModified: "2026-11-03",
            author: author
              ? { "@type": "Person", name: author.name, jobTitle: author.role }
              : { "@type": "Organization", name: SITE.name },
            reviewedBy: reviewer
              ? { "@type": "Person", name: reviewer.name, jobTitle: reviewer.role }
              : undefined,
            publisher: { "@id": `${SITE.url}/#organization` },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: guide.faq.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.url}/` },
              { "@type": "ListItem", position: 2, name: "20 euro storten", item: URL },
            ],
          }),
        },
      ],
    };
  },
  component: TwentyEuroPage,
});

function TwentyEuroPage() {
  const casinos = getPublicCasinos();
  const budget = new LinkBudget(PATH);

  const takeaways = [
    "Alle tien geteste aanbieders accepteren een storting van 20 euro; bij negen is het het kassaminimum.",
    "Geen enkele aanbieder rekende kosten voor een lage storting.",
    "iDEAL rondde de storting af in 9 tot 16 seconden; Trustly werkt ook als opnameroute.",
    "Twee aanbieders geven pas een welkomstbonus vanaf 25 of 50 euro.",
    "Het minimum opnamebedrag ligt tussen 10 en 20 euro; kijk daar vooraf naar.",
    "Bij 0,20 euro inzet per ronde gaat 20 euro gemiddeld zo'n 2.500 rondes mee.",
  ];

  return (
    <main className="mx-auto max-w-7xl px-4 py-4 sm:px-6 sm:py-12">
      <nav aria-label="Broodkruimels" className="mb-2 text-xs text-muted-foreground sm:mb-6 sm:text-sm">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">20 euro storten</span>
      </nav>

      <h1 className="max-w-4xl text-2xl font-bold leading-tight tracking-tight text-foreground sm:text-4xl">
        20 euro deposit casino's: de laagste storting die overal werkt
      </h1>

      <div className="mt-2 max-w-3xl sm:mt-6">
        <AuthorByline
          authorSlug={guide.authorSlug}
          reviewerSlug={guide.reviewerSlug}
          updatedAt={guide.updatedAt}
          readingMinutes={guide.readingMinutes}
        />
      </div>
      <ExpandableIntro>{guide.description}</ExpandableIntro>

      <div className="mt-8 hidden max-w-3xl sm:block">
        <KeyTakeaways items={takeaways} />
      </div>

      <div className="mt-4 grid gap-10 sm:mt-10 lg:grid-cols-[2fr_1fr]">
        <div className="min-w-0">
          <section aria-labelledby="casinos">
            <h2 id="casinos" className="text-xl font-semibold text-foreground">
              Beste 20 euro deposit casino&apos;s van november 2026
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Dezelfde vergelijkingslijst als elders op de site. Per aanbieder staan het gemeten
              kassaminimum en het minimum opnamebedrag verderop in dit artikel.
            </p>
            <div className="mt-4">
              <CasinoList casinos={casinos} />
            </div>
            <figure className="mt-6 overflow-hidden rounded-xl border border-border">
              <img
                src={ARTICLE_IMAGES.prepaid}
                alt="Illustratie van een casinostorting van 20 euro met budgetcontrole"
                width={1280}
                height={720}
                loading="lazy"
                decoding="async"
                className="aspect-[21/9] w-full object-cover"
              />
              <figcaption className="border-t border-border bg-secondary/50 px-4 py-2 text-xs text-muted-foreground">
                Beeld: redactie {SITE.name}
              </figcaption>
            </figure>
          </section>

          <div className="mt-10">
            <HowWeTested
              subject="een storting van precies 20 euro"
              providers={10}
              period="november 2026"
              extra="Per aanbieder is 20 euro gestort met de snelste beschikbare methode, minimaal veertig rondes gespeeld en daarna een opname aangevraagd tot het geld op onze eigen rekening stond."
            />
          </div>

          <section className="mt-12" aria-label="Alles over stortingen van 20 euro">
            <ArticleBody blocks={guide.blocks} currentPath={PATH} budget={budget} />
          </section>

          <div className="mt-14 grid gap-6">
            <PayoutSpeedInfographic />
          </div>

          <div className="mt-14">
            <EditorialTeam
              authorSlug={guide.authorSlug}
              reviewerSlug={guide.reviewerSlug}
              updatedAt={guide.updatedAt}
              methodName="lage stortingen"
            />
          </div>

          <section className="mt-12" aria-labelledby="faq">
            <h2 id="faq" className="text-xl font-semibold text-foreground">
              Veelgestelde vragen over 20 euro storten
            </h2>
            <Accordion type="single" collapsible className="mt-4">
              {guide.faq.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-sm font-medium">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground">
                    {linkifyText(item.a, budget, `faq-${i}`)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <div className="mt-14">
            <EeatTrustBlocks
              authorSlug={guide.authorSlug}
              reviewerSlug={guide.reviewerSlug}
              updatedAt={guide.updatedAt}
            />
          </div>
        </div>

        <aside className="min-w-0 space-y-6 lg:sticky lg:top-32 lg:self-start">
          <ArticleToc blocks={guide.blocks} />
          <div className="rounded-lg border border-border bg-secondary/50 p-5 text-xs leading-relaxed text-muted-foreground">
            {SITE.disclaimer}
          </div>
        </aside>
      </div>

      <div className="mt-14">
        <QualityGuarantee />
      </div>
    </main>
  );
}
